import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
} from '@mui/icons-material';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { apiService } from '../../services/api';

// ---------------------------------------------------------------------------
// Types — matches v2 /api/drift-trajectory response
// ---------------------------------------------------------------------------
interface TrajectoryPoint {
  day: number;
  date: string;
  cumulativePlanned: number;
  cumulativeActual: number;
  hasActuals: boolean;
}

interface ProjectedPoint {
  day: number;
  cumulativePages: number;
  isProjection: boolean;
}

interface Projection {
  currentDay: number;
  currentVelocity: number;
  projectedWrapDay: number;
  scheduledWrapDay: number;
  varianceDays: number;
  projectionStatus: string;
}

interface Metadata {
  totalDays: number;
  currentDay: number;
  totalScriptPages: number;
}

interface TrajectoryResponse {
  trajectory?: TrajectoryPoint[];
  projectedFuture?: ProjectedPoint[];
  projection?: Projection;
  metadata?: Metadata;
}

interface ChartPoint {
  day: number;
  date: string;
  planned: number | null;
  actual: number | null;
  projected: number | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface ProductionVelocityWidgetProps {
  widgetId: string;
  title: string;
}

const ProductionVelocityWidget: React.FC<ProductionVelocityWidgetProps> = ({
  widgetId,
  title,
}) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TrajectoryResponse | null>(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDriftTrajectoryData();
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.error || 'Failed to load velocity data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error fetching velocity data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      await apiService.refreshDriftTrajectory();
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error refreshing data');
    } finally {
      setRefreshing(false);
    }
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const trajectory      = data?.trajectory || [];
  const projectedFuture = data?.projectedFuture || [];
  const projection      = data?.projection;
  const metadata        = data?.metadata;

  const velocity         = projection?.currentVelocity || 0;
  const currentDay       = projection?.currentDay || metadata?.currentDay || 0;
  const totalDays        = metadata?.totalDays || 0;
  const projectedWrapDay = projection?.projectedWrapDay || totalDays;
  const scheduledWrapDay = projection?.scheduledWrapDay || totalDays;
  const daysVariance     = projectedWrapDay - scheduledWrapDay;
  const hasActuals       = currentDay > 0;
  const hasData          = trajectory.length > 0;

  // ── Build unified chart data ──────────────────────────────────────────────
  // Start with trajectory days
  const pointMap = new Map<number, ChartPoint>();
  trajectory.forEach(t => {
    pointMap.set(t.day, {
      day: t.day,
      date: t.date ? t.date.slice(5) : `D${t.day}`, // "MM-DD"
      planned: t.cumulativePlanned || null,
      actual: t.hasActuals ? t.cumulativeActual : null,
      projected: null,
    });
  });

  // Overlay projected future
  projectedFuture.forEach(p => {
    const existing = pointMap.get(p.day);
    if (existing) {
      existing.projected = p.cumulativePages;
    } else {
      pointMap.set(p.day, {
        day: p.day,
        date: `D${p.day}`,
        planned: null,
        actual: null,
        projected: p.cumulativePages,
      });
    }
  });

  const chartData: ChartPoint[] = Array.from(pointMap.values()).sort((a, b) => a.day - b.day);

  // ── Status helpers ────────────────────────────────────────────────────────
  const getStatusColor = () => {
    if (daysVariance < 0) return '#48BB78';
    if (daysVariance === 0) return '#4299E1';
    if (daysVariance <= 2) return '#ECC94B';
    return '#F56565';
  };

  const getStatusIcon = () => {
    if (daysVariance < 0) return <TrendingUp sx={{ color: '#48BB78' }} />;
    if (daysVariance === 0) return <TrendingFlat sx={{ color: '#4299E1' }} />;
    return <TrendingDown sx={{ color: '#F56565' }} />;
  };

  const getStatusLabel = () => {
    if (!hasActuals) return 'Awaiting DPRs';
    if (daysVariance < 0) return `${Math.abs(daysVariance)}d ahead`;
    if (daysVariance === 0) return 'On Schedule';
    return `${daysVariance}d behind`;
  };

  // ── Custom tooltip ────────────────────────────────────────────────────────
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <Box sx={{
        background: '#1A202C',
        border: '1px solid #4A5568',
        borderRadius: 1,
        p: 1.5,
        minWidth: 160,
      }}>
        <Typography variant="caption" sx={{ color: '#A0AEC0', display: 'block', mb: 0.5 }}>
          Day {label}
        </Typography>
        {payload.map((entry: any) =>
          entry.value != null ? (
            <Box key={entry.dataKey} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" sx={{ color: entry.color }}>{entry.name}</Typography>
              <Typography variant="caption" sx={{ color: '#E2E8F0', fontWeight: 'bold' }}>
                {Number(entry.value).toFixed(2)} pg
              </Typography>
            </Box>
          ) : null
        )}
      </Box>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Card sx={{ height: '100%', backgroundColor: '#2D3748', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #4A5568', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
          {hasData && (
            <Chip
              size="small"
              icon={getStatusIcon()}
              label={getStatusLabel()}
              sx={{
                backgroundColor: `${getStatusColor()}22`,
                color: getStatusColor(),
                fontWeight: 'bold',
                '& .MuiChip-icon': { color: getStatusColor() },
              }}
            />
          )}
        </Box>
        <IconButton size="small" onClick={handleRefresh} disabled={refreshing || loading} sx={{ color: '#A0AEC0' }}>
          <Refresh sx={{
            animation: refreshing ? 'spin 1s linear infinite' : 'none',
            '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
          }} />
        </IconButton>
      </Box>

      {/* Content */}
      <CardContent sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
            <CircularProgress size={40} />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && !hasData && (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex={1} sx={{ color: '#A0AEC0' }}>
            <Typography variant="body1" sx={{ mb: 1 }}>No production data yet</Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Data will appear once a schedule is processed
            </Typography>
          </Box>
        )}

        {!loading && !error && hasData && (
          <>
            {/* Summary Stats */}
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              {hasActuals ? (
                <>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>Current Velocity</Typography>
                    <Typography variant="h6" fontWeight="bold">{velocity.toFixed(2)} pg/day</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>Current Day</Typography>
                    <Typography variant="h6" fontWeight="bold">{currentDay} / {totalDays}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>Projected Wrap</Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: getStatusColor() }}>
                      Day {projectedWrapDay}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>Scheduled Days</Typography>
                    <Typography variant="h6" fontWeight="bold">{totalDays}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#A0AEC0' }}>Total Pages</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {metadata?.totalScriptPages?.toFixed(1) || '—'}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>

            {/* Legend pills */}
            <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 24, height: 2, background: 'repeating-linear-gradient(90deg, #4299E1 0px, #4299E1 6px, transparent 6px, transparent 12px)', borderRadius: 1 }} />
                <Typography variant="caption" sx={{ color: '#A0AEC0', fontSize: 10 }}>● Plan</Typography>
              </Box>
              {hasActuals && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 24, height: 3, background: '#48BB78', borderRadius: 1 }} />
                  <Typography variant="caption" sx={{ color: '#A0AEC0', fontSize: 10 }}>● Actual</Typography>
                </Box>
              )}
              {projectedFuture.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 24, height: 2, background: 'repeating-linear-gradient(90deg, #ED8936 0px, #ED8936 3px, transparent 3px, transparent 6px)', borderRadius: 1 }} />
                  <Typography variant="caption" sx={{ color: '#A0AEC0', fontSize: 10 }}>● Projected</Typography>
                </Box>
              )}
            </Box>

            {/* Chart */}
            <Box sx={{ flex: 1, minHeight: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3D4A5C" />
                  <XAxis
                    dataKey="day"
                    stroke="#A0AEC0"
                    tick={{ fill: '#A0AEC0', fontSize: 11 }}
                    tickFormatter={(v) => `D${v}`}
                  />
                  <YAxis
                    stroke="#A0AEC0"
                    tick={{ fill: '#A0AEC0', fontSize: 11 }}
                    width={40}
                    label={{ value: 'Pages', angle: -90, position: 'insideLeft', fill: '#718096', fontSize: 10 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="top"
                    height={28}
                    wrapperStyle={{ fontSize: '11px', color: '#A0AEC0' }}
                  />

                  {/* Plan line */}
                  <Line
                    type="monotone"
                    dataKey="planned"
                    name="Plan"
                    stroke="#4299E1"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    dot={false}
                    connectNulls
                    opacity={0.85}
                  />

                  {/* Actual line */}
                  {hasActuals && (
                    <Line
                      type="monotone"
                      dataKey="actual"
                      name="Actual"
                      stroke="#48BB78"
                      strokeWidth={3}
                      dot={{ fill: '#48BB78', r: 3 }}
                      connectNulls={false}
                      opacity={1}
                    />
                  )}

                  {/* Projected line */}
                  {projectedFuture.length > 0 && (
                    <Line
                      type="monotone"
                      dataKey="projected"
                      name="Projected"
                      stroke="#ED8936"
                      strokeWidth={2}
                      strokeDasharray="3 2"
                      dot={false}
                      connectNulls={false}
                      opacity={0.8}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            {!hasActuals && (
              <Typography variant="caption" sx={{ color: '#4A5568', textAlign: 'center', mt: 1 }}>
                Actuals and projection will appear once DPRs are processed
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductionVelocityWidget;
